import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authService, AdminUser } from '../services/auth'
import toast from 'react-hot-toast'

interface AuthContextType {
  user: AdminUser | null
  loading: boolean
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  isAdmin: boolean
  checkAdminStatus: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    // Initialize auth service
    authService.initializeFromStorage()
    
    // Set initial user state
    const initialUser = authService.getCurrentUser()
    setUser(initialUser)
    
    // Listen for auth state changes
    const unsubscribe = authService.onAuthStateChange(async (newUser) => {
      setUser(newUser)
      
      if (newUser) {
        // Add a small delay to ensure token is properly set
        setTimeout(async () => {
          // Check if user is admin
          const adminStatus = await authService.checkAdminStatus()
          setIsAdmin(adminStatus)
          
          if (!adminStatus) {
            toast.error('Access denied. You are not authorized to use the admin console.')
            await authService.signOut()
            setUser(null)
            setIsAdmin(false)
          }
          
          setLoading(false)
        }, 500)
      } else {
        setIsAdmin(false)
        setLoading(false)
      }
    })

    return unsubscribe
  }, [])

  const signInWithGoogle = async () => {
    try {
      setLoading(true)
      await authService.signInWithGoogle()
      
      // Check admin status after successful sign-in
      const adminStatus = await authService.checkAdminStatus()
      setIsAdmin(adminStatus)
      
      if (!adminStatus) {
        toast.error('Access denied. You are not authorized to use the admin console.')
        await authService.signOut()
        setUser(null)
        setIsAdmin(false)
      } else {
        toast.success('Signed in successfully')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign in with Google')
    } finally {
      setLoading(false)
    }
  }


  const signOut = async () => {
    try {
      setLoading(true)
      await authService.signOut()
      toast.success('Signed out successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign out')
    } finally {
      setLoading(false)
    }
  }

  const checkAdminStatus = async (): Promise<boolean> => {
    const adminStatus = await authService.checkAdminStatus()
    setIsAdmin(adminStatus)
    return adminStatus
  }

  const value: AuthContextType = {
    user,
    loading,
    signInWithGoogle,
    signOut,
    isAdmin,
    checkAdminStatus,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
